import { createFileRoute } from "@tanstack/react-router";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "@/hooks/useForm";
import { registrationSchema } from "@shared/validation/composites/auth-fields";
import { Link } from "@tanstack/react-router";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { signInWithPopup } from "firebase/auth";
import { auth, googleProvider } from "@/lib/firebase";
import {
  signUp,
  signInWithFirebase,
  ApiError,
  getPostAuthRedirect,
} from "@/lib/api/auth";
import { VerifyEmailStep } from "@/components/auth/verify-email-step";
import { Button } from "@/components/ui/button";
import { FormLabel } from "@/components/ui/form-label";
import { Input } from "@/components/ui/input";
import { InputWrapper } from "@/components/ui/input-wrapper";
import { Icon } from "@/components/ui/icons";
import { Text } from "@/components/ui/text";
import { HStack } from "@/components/ui/h-stack";
import { lazyComponent } from "@/lib/lazy-component";
const DatePicker = lazyComponent(() =>
  import("@/components/ui/date-picker").then((m) => ({
    default: m.DatePicker,
  })),
);
import { Heading } from "@/components/ui/heading";
import { Separator } from "@/components/ui/separator";
import { PasswordRequirements } from "@/components/ui/password-requirements";
import { VStack } from "@/components/ui/v-stack";

type RegisterView = "form" | "verify";

type RegFormState = {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  phone: string;
  birthDate: string;
};

function RegisterPage() {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { email?: string };
  const { t: tAuth } = useTranslation("auth");
  const { t: tCommon } = useTranslation("common");
  const { t: tError } = useTranslation("error");

  const [view, setView] = React.useState<RegisterView>("form");
  const [error, setError] = React.useState("");
  const [isGoogleLoading, setIsGoogleLoading] = React.useState(false);

  const form = useForm<RegFormState>({
    schema: registrationSchema(),
    defaults: {
      name: "",
      email: "",
      password: "",
      confirmPassword: "",
      phone: "",
      birthDate: "",
    },
    normalize: (d) => ({
      name: d.name.trim(),
      email: d.email.trim(),
      password: d.password,
      confirmPassword: d.confirmPassword,
      phone: d.phone.trim(),
      birthDate: d.birthDate,
    }),
    onSubmit: async (data) => {
      setError("");
      try {
        const result = await signUp({
          name: data.name,
          email: data.email,
          password: data.password,
          phone_number: data.phone,
          birth_date: data.birthDate,
        });

        if (result.successful) {
          setView("verify");
        } else {
          setError(tAuth("failedToSendVerification"));
        }
      } catch (err) {
        if (err instanceof ApiError && !err.fields) {
          // Non-field API errors (e.g. EMAIL_ALREADY_REGISTERED 409): prefer a
          // localized message keyed by the error code, falling back to the raw
          // server message for codes we haven't translated yet.
          setError(
            err.code
              ? tError(err.code as Parameters<typeof tError>[0], {
                  defaultValue: err.message,
                })
              : err.message,
          );
        } else if (!(err instanceof ApiError)) {
          setError(tCommon("errorOccurred"));
        }
        throw err;
      }
    },
  });

  React.useEffect(() => {
    const prefillEmail = search.email;
    if (prefillEmail && !form.fields.email) {
      form.setField("email", prefillEmail.trim());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.email]);

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await form.submitSafe();
  };

  const handleGoogleSignUp = async () => {
    setIsGoogleLoading(true);
    setError("");

    try {
      const result = await signInWithPopup(auth, googleProvider);
      const idToken = await result.user.getIdToken();
      const userEmail = result.user.email;

      if (!userEmail) {
        throw new Error("No email returned from Google sign-in");
      }

      const user = await signInWithFirebase({
        id_token: idToken,
        email: userEmail,
      });

      void navigate({ to: (await getPostAuthRedirect(user)) as never });
    } catch (err) {
      console.error("Google sign-up error:", err);
      if (err instanceof ApiError) {
        setError(err.message);
      } else if (err instanceof Error) {
        if (!err.message.includes("popup-closed")) {
          setError(err.message);
        }
      } else {
        setError(tCommon("errorOccurred"));
      }
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const isLoading = form.isSubmitting || isGoogleLoading;

  if (view === "verify") {
    return (
      <VerifyEmailStep
        email={form.fields.email}
        onSuccess={(path) => void navigate({ to: path as never })}
        onResendCode={async () => {
          const result = await signUp({
            name: form.fields.name,
            email: form.fields.email,
            password: form.fields.password,
            phone_number: form.fields.phone,
            birth_date: form.fields.birthDate,
          });
          if (result.successful) {
            alert(tAuth("codeSent"));
          } else {
            throw new Error(tCommon("error"));
          }
        }}
        onBack={() => setView("form")}
      />
    );
  }

  return (
    <VStack gap="none">
      <Heading size="xl" className="mb-2">
        {tAuth("createAccount")}
      </Heading>
      <Text color="muted" className="mb-6">
        {tAuth("joinYuklar")}
      </Text>

      <Button
        color="neutral"
        variant="outline"
        size="lg"
        fullWidth
        onClick={handleGoogleSignUp}
        disabled={isLoading}
        className="mb-4"
      >
        <Icon.Google className="w-5 h-5 mr-3" />
        {tAuth("continueWithGoogle")}
      </Button>

      <HStack className="my-6">
        <Separator className="flex-1" />
        <Text color="muted" size="sm" className="px-2">
          {tAuth("orRegisterWith")}
        </Text>
        <Separator className="flex-1" />
      </HStack>

      <form onSubmit={handleFormSubmit} className="space-y-4">
        <VStack gap="none">
          <FormLabel htmlFor="name" required>
            {tAuth("fullName")}
          </FormLabel>
          <InputWrapper id="name" error={form.fieldError("name")}>
            <Input
              id="name"
              type="text"
              value={form.fields.name}
              onChange={(e) => form.setField("name", e.target.value)}
              onBlur={() => form.touchField("name")}
              placeholder={tAuth("fullName")}
              size="lg"
              fullWidth
              required
              data-testid="register-name-input"
            />
          </InputWrapper>
        </VStack>

        <VStack gap="none">
          <FormLabel htmlFor="email" required>
            {tAuth("email")}
          </FormLabel>
          <InputWrapper
            id="email"
            error={form.fieldError("email")}
            data-testid="email"
          >
            <Input
              id="email"
              type="email"
              value={form.fields.email}
              onChange={(e) => form.setField("email", e.target.value)}
              onBlur={() => form.touchField("email")}
              placeholder="you@example.com"
              size="lg"
              fullWidth
              required
              data-testid="register-email-input"
            />
          </InputWrapper>
        </VStack>

        <VStack gap="none">
          <FormLabel htmlFor="phone" required>
            {tAuth("phoneNumber")}
          </FormLabel>
          <InputWrapper id="phone" error={form.fieldError("phone")}>
            <Input
              id="phone"
              type="tel"
              value={form.fields.phone}
              onChange={(e) => form.setField("phone", e.target.value)}
              onBlur={() => form.touchField("phone")}
              placeholder={tAuth("phonePlaceholder")}
              size="lg"
              fullWidth
              required
              data-testid="register-phone-input"
            />
          </InputWrapper>
        </VStack>

        <VStack gap="none">
          <FormLabel htmlFor="birthDate" required>
            {tAuth("dateOfBirth")}
          </FormLabel>
          <DatePicker
            value={form.fields.birthDate}
            onChange={(value) => {
              form.setField("birthDate", value);
              form.touchField("birthDate");
            }}
            data-testid="birth-date-input"
          />
          {form.fieldError("birthDate") && (
            <Text size="xs" color="danger" className="mt-1" role="alert">
              {form.fieldError("birthDate")}
            </Text>
          )}
        </VStack>

        <VStack gap="none">
          <FormLabel htmlFor="password" required>
            {tAuth("password")}
          </FormLabel>
          <Input
            id="password"
            type="password"
            value={form.fields.password}
            onChange={(e) => form.setField("password", e.target.value)}
            onBlur={() => form.touchField("password")}
            placeholder={tAuth("atLeastChars")}
            size="lg"
            fullWidth
            required
            data-testid="register-password-input"
          />
          <PasswordRequirements
            value={form.fields.password}
            show={
              !!form.fieldError("password") || form.fields.password.length > 0
            }
          />
        </VStack>

        <VStack gap="none">
          <FormLabel htmlFor="confirmPassword" required>
            {tAuth("confirmPassword")}
          </FormLabel>
          <InputWrapper
            id="confirmPassword"
            error={form.fieldError("confirmPassword")}
          >
            <Input
              id="confirmPassword"
              type="password"
              value={form.fields.confirmPassword}
              onChange={(e) => form.setField("confirmPassword", e.target.value)}
              onBlur={() => form.touchField("confirmPassword")}
              placeholder={tAuth("confirmPassword")}
              size="lg"
              fullWidth
              required
              data-testid="register-confirm-password-input"
            />
          </InputWrapper>
        </VStack>

        {error && <Text color="danger">{error}</Text>}

        <Button
          type="submit"
          size="lg"
          fullWidth
          disabled={!form.isValid || isLoading}
          data-testid="register-submit-btn"
        >
          {isLoading ? (
            <HStack justify="center">
              <Icon.Spinner size="md" className="mr-2" />
              {tAuth("creatingAccount")}
            </HStack>
          ) : (
            tAuth("createAccount")
          )}
        </Button>
      </form>

      <Text color="muted" className="mt-6" align="center">
        {tAuth("alreadyHaveAccount")}{" "}
        <Link
          to="/login"
          className="text-[color:var(--primary)] hover:opacity-80 font-medium"
        >
          {tAuth("signIn")}
        </Link>
      </Text>
    </VStack>
  );
}

export const Route = createFileRoute("/_auth/register")({
  component: RegisterPage,
});
